'use client'
import {
  useRedeem
} from "./chunk-7OO2W25G.mjs";
import {
  usePayment
} from "./chunk-QH2UTQBS.mjs";

// src/components/booking/totals/BookingTotals.tsx
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { formatPrice as formatPrice2, getSafeCurrencySymbol } from "@wbk/utils";
import { CurrencyPriceLabel as CurrencyPriceLabel2, CurrencySymbol, useCurrency } from "@wbk/currency";
import { ChevronIcon, ExclamationIcon } from "@wbk/svg";
import { Trans as Trans2, useRouter, useTranslation as useTranslation2 } from "@wbk/config";
import { Button, Divider, Typography as Typography3 } from "@wbk/ui";
import { motion } from "motion/react";

// src/components/booking/saudiRewards/Ribbon.tsx
import { formatPrice } from "@wbk/utils";
import { Trans } from "@wbk/config";
import { jsx, jsxs } from "react/jsx-runtime";
var SaudiRewardsRibbon = ({ totals }) => {
  const { saudi_redeem } = useRedeem();
  const earnedPoints = Math.floor(totals.net_after_discount);
  const verifiedButNotEarning = !!saudi_redeem && totals.net_after_discount <= 0;
  if (totals.net_after_discount <= 0 || verifiedButNotEarning || !saudi_redeem) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: "relative !mt-4 flex items-center gap-2 rounded-md bg-violet-950 p-2 text-sm text-violet-300", children: [
    /* @__PURE__ */ jsx("span", { className: "text-lg", children: "\u{1F389}" }),
    /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
      Trans,
      {
        i18nKey: "payment_common:earned",
        values: { earnedPoints: formatPrice(earnedPoints), count: earnedPoints },
        children: /* @__PURE__ */ jsx("span", { className: "text-orange-500" })
      }
    ) }),
    /* @__PURE__ */ jsx("span", { className: "absolute top-0 h-3 w-3 -translate-y-1.5 rotate-45 bg-violet-950 ltr:right-7 rtl:left-7" })
  ] });
};
var Ribbon_default = SaudiRewardsRibbon;

// src/components/booking/bundle/Ribbon.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var BundleDiscountRibbon = ({ discount }) => {
  if (!discount) {
    return null;
  }
  return /* @__PURE__ */ jsxs2("div", { className: "relative !mt-4 flex items-center gap-2 rounded-md bg-[#27272A] p-2 text-sm", children: [
    /* @__PURE__ */ jsx2("span", { className: "text-lg", children: "\u{1F389}" }),
    /* @__PURE__ */ jsx2("p", { children: discount })
  ] });
};
var Ribbon_default2 = BundleDiscountRibbon;

// src/components/booking/subscription/CreditsToggler.tsx
import { useEffect } from "react";
import { SubscriptionIcon } from "@wbk/svg";
import { Switch, Typography } from "@wbk/ui";
import { useTranslation } from "@wbk/config";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var SubscriptionCreditsToggler = ({ usedCredits, remainingCredits, plan }) => {
  const { isSubscriptionCreditsEnabled, setSubscriptionCreditsEnabled } = usePayment();
  const { t } = useTranslation("ticketing_booking");
  const noBenefits = !remainingCredits && !usedCredits;
  const disabled = !remainingCredits;
  useEffect(() => {
    if (remainingCredits && isSubscriptionCreditsEnabled === void 0) {
      setSubscriptionCreditsEnabled(true);
    }
  }, [remainingCredits]);
  if (noBenefits)
    return null;
  return /* @__PURE__ */ jsxs3(
    "label",
    {
      "data-disabled": disabled,
      className: "space-between flex cursor-pointer items-center justify-between gap-2 rounded-xl data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-50",
      children: [
        /* @__PURE__ */ jsx3(SubscriptionIcon, { className: "h-6 w-6" }),
        /* @__PURE__ */ jsxs3("div", { children: [
          /* @__PURE__ */ jsx3(Typography, { variant: "label-M", children: t("payment_common:use_membership_benefits", { plan }) }),
          /* @__PURE__ */ jsx3(Typography, { variant: "body-S", color: "text-secondary", children: remainingCredits > 1e4 ? t("payment_common:unlimited_streams") : t("payment_common:available_streams", { count: remainingCredits }) })
        ] }),
        /* @__PURE__ */ jsx3(
          Switch,
          {
            checked: isSubscriptionCreditsEnabled,
            onChange: () => setSubscriptionCreditsEnabled(!isSubscriptionCreditsEnabled),
            disabled,
            hideErrorSpace: true,
            groupClassName: "ltr:ml-auto rtl:mr-auto"
          }
        )
      ]
    }
  );
};
var CreditsToggler_default = SubscriptionCreditsToggler;

// src/components/booking/totals/TotalFees.tsx
import { Tooltip, Typography as Typography2 } from "@wbk/ui";
import { CurrencyPriceLabel } from "@wbk/currency";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var TotalFees = ({ title, description, amount, currency }) => {
  return /* @__PURE__ */ jsxs4(Typography2, { as: "div", variant: "body-S", className: "flex items-center justify-between", children: [
    /* @__PURE__ */ jsxs4("div", { className: "flex w-full grow items-center gap-2", children: [
      /* @__PURE__ */ jsx4("p", { children: title }),
      description && /* @__PURE__ */ jsx4("div", { className: "relative grow", children: /* @__PURE__ */ jsx4(
        Tooltip,
        {
          className: "p-2",
          side: "bottom",
          content: /* @__PURE__ */ jsx4("div", { className: "text-xs", dangerouslySetInnerHTML: { __html: description } }),
          children: /* @__PURE__ */ jsx4("span", { className: "font-inter bg-paper-level-1 focus:bg-paper-level-2 flex h-5 w-5 items-center justify-center rounded-full focus:outline-none", children: "i" })
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx4(CurrencyPriceLabel, { className: "shrink-0", originalPrice: amount, currency })
  ] });
};
var TotalFees_default = TotalFees;

// src/components/booking/totals/BookingTotals.tsx
import { Fragment, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var BookingTotals = ({ className, totals, discount, subscriptionCredits }) => {
  const { event, currency: eventCurrency = "SAR", isSubscriptionCreditsEnabled } = usePayment();
  const { locale } = useRouter();
  const { currency, active, currencies } = useCurrency();
  const { coupon, saudi_redeem, enable_wallet_credits } = useRedeem();
  const { t } = useTranslation2("payment_common");
  const [expanded, setExpanded] = useState(false);
  const hasCouponDiscount = coupon.discount > 0 && !!totals.discount_total;
  const hasSaudiRewardsDiscount = !!(saudi_redeem == null ? void 0 : saudi_redeem.quantity);
  const organizerCurrency = currencies.find((c) => c.shortCode === eventCurrency);
  return /* @__PURE__ */ jsxs5("div", { className: twMerge("space-y-2", className), children: [
    subscriptionCredits && /* @__PURE__ */ jsxs5(Fragment, { children: [
      /* @__PURE__ */ jsx5(
        CreditsToggler_default,
        {
          usedCredits: subscriptionCredits.used,
          remainingCredits: subscriptionCredits.remaining,
          plan: subscriptionCredits.plan
        }
      ),
      /* @__PURE__ */ jsx5("hr", { className: "border-paper !mt-4 border-dashed" })
    ] }),
    enable_wallet_credits && !!totals.withdrawn_wallet_credits && /* @__PURE__ */ jsxs5(
      Typography3,
      {
        as: "div",
        variant: "body-S",
        color: "success",
        className: "flex items-center justify-between",
        children: [
          /* @__PURE__ */ jsx5("p", { children: t("payment_common:webook_credit") }),
          /* @__PURE__ */ jsx5(CurrencyPriceLabel2, { originalPrice: -totals.withdrawn_wallet_credits, currency: "SAR" })
        ]
      }
    ),
    /* @__PURE__ */ jsx5("div", { children: /* @__PURE__ */ jsxs5(
      Typography3,
      {
        as: "object",
        variant: "body-L",
        className: "flex w-full flex-wrap items-center justify-between",
        strong: true,
        children: [
          /* @__PURE__ */ jsx5(
            Button,
            {
              shape: "text",
              theme: "transparent",
              className: "group p-px",
              "aria-expanded": expanded,
              onClick: () => setExpanded((prev) => !prev),
              children: /* @__PURE__ */ jsxs5("p", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx5("span", { children: t("payment_common:total") }),
                /* @__PURE__ */ jsx5(ChevronIcon, { className: "h-3 w-3 rotate-90 transition-transform group-aria-[expanded=true]:-rotate-90" })
              ] })
            }
          ),
          /* @__PURE__ */ jsxs5("div", { className: "flex flex-col items-end", children: [
            /* @__PURE__ */ jsx5(
              CurrencyPriceLabel2,
              {
                originalPrice: totals.net_after_discount,
                currency: eventCurrency,
                className: "text-xl",
                changeable: true
              }
            ),
            /* @__PURE__ */ jsx5(Typography3, { variant: "body-XS", color: "text-secondary", as: "span", children: t("payment_common:vat_included") })
          ] })
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs5(
      motion.div,
      {
        initial: { height: 0 },
        animate: { height: expanded ? "auto" : 0 },
        transition: { duration: 0.2 },
        className: "space-y-3 overflow-hidden",
        children: [
          /* @__PURE__ */ jsxs5(Typography3, { as: "div", variant: "body-S", className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx5("p", { children: t("payment_common:subtotal") }),
            /* @__PURE__ */ jsx5(CurrencyPriceLabel2, { originalPrice: totals.gross, currency: eventCurrency })
          ] }),
          !!totals.discount_total && /* @__PURE__ */ jsxs5(
            Typography3,
            {
              as: "div",
              variant: "body-S",
              color: "success",
              className: "flex items-center justify-between",
              children: [
                /* @__PURE__ */ jsx5("p", { children: hasCouponDiscount ? `${t("payment_common:promo_code")} (${coupon.promo_code})` : t("payment_common:discount") }),
                /* @__PURE__ */ jsx5(CurrencyPriceLabel2, { originalPrice: -totals.discount_total, currency: eventCurrency })
              ]
            }
          ),
          event.fees && !!totals.totalFee && /* @__PURE__ */ jsx5(
            TotalFees_default,
            {
              title: event.fees.title,
              description: event.fees.description,
              amount: totals.totalFee,
              currency: eventCurrency
            }
          ),
          !!totals.vat && /* @__PURE__ */ jsxs5(Typography3, { as: "div", variant: "body-S", className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx5("p", { children: `${t("payment_common:vat")} ${(event == null ? void 0 : event.vat_percent) ? `${event == null ? void 0 : event.vat_percent}%` : ""}` }),
            /* @__PURE__ */ jsx5(CurrencyPriceLabel2, { originalPrice: totals.vat, currency: eventCurrency })
          ] }),
          hasSaudiRewardsDiscount && !!totals.withdrawn_saudi_rewards && /* @__PURE__ */ jsxs5(
            Typography3,
            {
              as: "div",
              variant: "body-S",
              color: "success",
              className: "flex items-center justify-between",
              children: [
                /* @__PURE__ */ jsxs5("p", { children: [
                  t("payment_common:saudi_rewards"),
                  " (",
                  formatPrice2(saudi_redeem.quantity),
                  ")"
                ] }),
                /* @__PURE__ */ jsx5(
                  CurrencyPriceLabel2,
                  {
                    originalPrice: -totals.withdrawn_saudi_rewards,
                    currency: eventCurrency
                  }
                )
              ]
            }
          ),
          isSubscriptionCreditsEnabled && !!totals.withdrawn_subscription_credits && /* @__PURE__ */ jsxs5(
            Typography3,
            {
              as: "div",
              variant: "body-S",
              color: "success",
              className: "flex items-center justify-between",
              children: [
                /* @__PURE__ */ jsx5("p", { children: t("payment_common:streaming_credits") }),
                /* @__PURE__ */ jsx5(
                  CurrencyPriceLabel2,
                  {
                    originalPrice: -totals.withdrawn_subscription_credits,
                    currency: eventCurrency
                  }
                )
              ]
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx5(Ribbon_default, { totals }),
    !!discount && /* @__PURE__ */ jsx5(Ribbon_default2, { discount }),
    active && eventCurrency !== currency.shortCode && /* @__PURE__ */ jsxs5("div", { className: "space-y-5 pt-2", children: [
      /* @__PURE__ */ jsx5(Divider, {}),
      /* @__PURE__ */ jsxs5("div", { className: "flex items-center justify-between py-1", children: [
        /* @__PURE__ */ jsx5(Typography3, { strong: true, children: t("payment_common:organizer_currency") }),
        /* @__PURE__ */ jsx5(
          Typography3,
          {
            className: "text-end",
            strong: true,
            children: `${formatPrice2(totals.net_after_discount)} ${getSafeCurrencySymbol({ currencyCode: eventCurrency, locale })}`
          }
        )
      ] }),
      /* @__PURE__ */ jsx5(Divider, {}),
      /* @__PURE__ */ jsxs5("div", { className: "text-text-secondary flex items-center gap-2 text-sm", children: [
        /* @__PURE__ */ jsx5(ExclamationIcon, { className: "h-5 w-5 shrink-0" }),
        /* @__PURE__ */ jsx5(Typography3, { variant: "body-S", children: t("payment_common:organizer_currency_note1") })
      ] }),
      /* @__PURE__ */ jsxs5("div", { className: "text-text-secondary flex items-center gap-2", children: [
        /* @__PURE__ */ jsx5(ExclamationIcon, { className: "h-5 w-5 shrink-0" }),
        /* @__PURE__ */ jsx5(Typography3, { variant: "body-S", children: /* @__PURE__ */ jsxs5(
          Trans2,
          {
            i18nKey: "organizer_currency_note2",
            ns: "payment_common",
            values: {
              actual: getSafeCurrencySymbol({ currencyCode: eventCurrency, locale })
            },
            children: [
              /* @__PURE__ */ jsx5(CurrencySymbol, { className: "inline" }),
              /* @__PURE__ */ jsx5(CurrencySymbol, { className: "inline", currency: organizerCurrency })
            ]
          }
        ) })
      ] })
    ] })
  ] });
};
var BookingTotals_default = BookingTotals;

export {
  TotalFees_default,
  BookingTotals_default
};
//# sourceMappingURL=chunk-YWNMR6FQ.mjs.map